import { Field, Form, Formik, FormikErrors } from 'formik';
import { useNavigate } from 'react-router-dom';
import { db } from '../instant';
import { id, TransactionChunk } from '@instantdb/core';
import { Alert, Button, Link, Paper, Stack, Typography, Box, ToggleButton, Divider, SxProps, Theme, Snackbar } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import LoadingIndicator from './LoadingIndicator';
import { createMaps, getImageUrl, uploadImage, cacheImage, deleteCachedImage } from '../upload';
import AddNewMaps from './AddNewMaps';
import { useEventDetails } from '../context';
import MapList from './MapList';
import FormTextInput from './FormTextInput';
import { AppSchema } from '@zw-app/shared/instant.schema';
import DiscardChangesDialog from './DiscardChangesDialog';
import DirtyChecker from './DirtyChecker';
import { CustomMaterialSchema, eventFormSchema, EventFormValues } from 'src/schema';
import DatasetList from './DatasetList';
import CustomMaterialList from './CustomMaterialList';
import { getOneLink } from 'src/utils';
import { ClientLogoViewMode, ClientLogoEditMode } from './ClientLogo';
import WelcomeDocInput from './WelcomeDocInput';
import { buildWelcomeDocViewUrl, parseWelcomeDocUrl } from '../utils/welcomeDocUrl';
import { format } from 'date-fns';
import EventDatePickers from './EventDatePickers';
import { usePermissions } from 'src/hooks/usePermissions';

interface Props {
    eventId?: string;
    viewOnly?: boolean;
    inTab?: boolean;
    centeredHeaders?: boolean;
    groupId?: string;
}

/** Flatten nested Formik/Yup errors into readable lines (field path + message). */
function flattenFormikErrors(errors: unknown, prefix = ''): string[] {
    if (errors == null || errors === '') return [];
    if (typeof errors === 'string') return [prefix ? `${prefix}: ${errors}` : errors];
    if (Array.isArray(errors)) {
        return errors.flatMap((e, i) => flattenFormikErrors(e, `${prefix}[${i}]`));
    }
    if (typeof errors === 'object') {
        return Object.entries(errors as Record<string, unknown>).flatMap(([k, v]) => flattenFormikErrors(v, prefix ? `${prefix}.${k}` : k));
    }
    return [];
}

function formatUserSaveError(err: unknown): string {
    if (err instanceof Error) {
        const m = err.message;
        if (m === 'no token!') return 'You must be signed in to save changes.';
        if (m.includes('timed out after')) {
            return 'Saving took too long. Check your connection and try again.';
        }
        if (m.length > 280) return 'Could not save changes. Please try again.';
        return m;
    }
    return 'Could not save changes. Please try again.';
}

function EventFormValidationNotifier({
    submitCount,
    errors,
    eventId,
    onValidationFailed,
}: {
    submitCount: number;
    errors: FormikErrors<EventFormValues>;
    eventId: string;
    onValidationFailed: (message: string) => void;
}) {
    const lastHandledSubmitCount = useRef(0);
    useEffect(() => {
        if (submitCount <= lastHandledSubmitCount.current) {
            return;
        }
        const lines = flattenFormikErrors(errors);
        if (lines.length === 0) {
            lastHandledSubmitCount.current = submitCount;
            return;
        }
        lastHandledSubmitCount.current = submitCount;
        const detail = lines.slice(0, 2).join(' ');
        const suffix = lines.length > 2 ? ` (+${lines.length - 2} more)` : '';
        onValidationFailed(`Some fields are invalid. ${detail}${suffix}`);
        if (process.env.NODE_ENV === 'development') {
            console.error('[EventForm] validation failed — save was not submitted', {
                eventId,
                submitCount,
                errors,
                flattened: lines,
            });
        }
    }, [submitCount, errors, eventId, onValidationFailed]);
    return null;
}

export default function EventForm(props: Props) {
    const { eventId, viewOnly = false, inTab = false, centeredHeaders = false, groupId } = props;
    const isCreating = eventId === undefined;
    const shouldCenterHeaders = (isCreating && inTab) || centeredHeaders;
    const navigate = useNavigate();
    const { canEditEvents, canEditMap } = usePermissions();
    const { user, isLoading: isAuthLoading, error: authError } = db.useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [editMode, setEditMode] = useState(!eventId && canEditEvents);
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const { event, isLoading: eventLoading, error: eventError } = useEventDetails(eventId);
    const existingMaps = event?.maps || [];
    const numOtherMaps = event ? existingMaps.length : 0;

    const targetEventId = useMemo(() => eventId || id(), [eventId]);

    // Query event group if groupId is provided (for creating events in a group)
    // Use a memoized targetGroupId to prevent infinite re-renders, similar to EventGroupForm
    // When groupId is not provided, use a non-existent ID so the query returns empty results
    const targetGroupId = useMemo(() => groupId || id(), [groupId]);
    const { data: groupData } = db.useQuery({
        eventGroups: {
            $: {
                where: {
                    id: targetGroupId,
                },
            },
            events: {
                $: { order: { order: 'asc' } },
            },
        },
    });

    const group = groupId && isCreating && groupData?.eventGroups ? getOneLink(groupData.eventGroups) : null;
    const groupEvents = group?.events || [];
    const newEventOrder = groupEvents.length;

    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
    const [_clientLogo, setClientLogo] = useState<File | null>(null);
    /** True only when the user selected a new logo file this session (so we don't upload unless they changed it). */
    const [userSelectedNewLogo, setUserSelectedNewLogo] = useState(false);

    const discard = useCallback(() => {
        setClientLogo(null);
        setUserSelectedNewLogo(false);
        setEditMode(false);
        setHasBeenEdited(false);
    }, []);

    const closeDiscardDialog = useCallback(() => {
        setDiscardDialogOpen(false);
    }, [setDiscardDialogOpen]);

    const showSaveError = useCallback((message: string) => {
        setSaveErrorMessage(message);
    }, []);

    const toggleEditMode = useCallback(() => {
        if (!canEditEvents) return;
        if (editMode && hasBeenEdited) {
            setDiscardDialogOpen(true);
        } else {
            setEditMode(!editMode);
        }
    }, [editMode, hasBeenEdited, canEditEvents, setEditMode, setDiscardDialogOpen]);

    const isLoading = isAuthLoading || eventLoading;
    const error = authError || eventError;

    if (isLoading) {
        return <LoadingIndicator message="Loading event details..." />;
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    const customMaterials: CustomMaterialSchema[] =
        (event?.customMaterials ?? [])
            .map(m => {
                const category = getOneLink(m.category);
                return category
                    ? {
                          id: m.id,
                          custom: m.custom,
                          name: m.name,
                          order: m.order,
                          categoryId: category.id,
                          lbsPerYd3: m.lbsPerYd3 || null,
                      }
                    : undefined;
            })
            .filter(m => m !== undefined) || [];

    const initialValues: EventFormValues = event
        ? {
              eventName: event.name,
              existingMaps: event.maps ?? [],
              datasets: event.datasets ?? [],
              customMaterials,
              newMaps: [],
              clientLogo: null,
              startDate: event.startDate ? new Date(event.startDate) : null,
              endDate: event.endDate ? new Date(event.endDate) : null,
              clientName: event.clientName || null,
              location: event.location || null,
              welcomeDocId: (event as { welcomeDocId?: string | null }).welcomeDocId ?? null,
              welcomeTabId: (event as { welcomeTabId?: string | null }).welcomeTabId ?? null,
              welcomeDocUrlRaw: null,
          }
        : {
              eventName: '',
              existingMaps: [],
              datasets: [{ order: 0, name: 'default' }],
              customMaterials,
              newMaps: [],
              clientLogo: null,
              startDate: null,
              endDate: null,
              clientName: null,
              location: null,
              welcomeDocId: null,
              welcomeTabId: null,
              welcomeDocUrlRaw: null,
          };

    const maybeUploadSpinner = isUploading ? <LoadingIndicator message={eventId ? 'Updating event...' : 'Creating event...'} /> : null;
    const canEdit = !isCreating && !viewOnly && user !== null;

    const subtitle = isCreating ? 'Create Event' : editMode ? 'Edit Event' : 'Event Properties';

    const renderBasicInfoContent = () => (
        <Stack spacing={2}>
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Event name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {String(event?.name)}
                </Typography>
            </Box>

            {event?.startDate ? (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {event.endDate ? 'Event dates' : 'Event date'}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {format(new Date(event.startDate), 'M/d/yy')}
                        {event.endDate ? ` - ${format(new Date(event.endDate), 'M/d/yy')}` : ''}
                    </Typography>
                </Box>
            ) : null}

            {event?.clientName ? (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Client
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {String(event.clientName)}
                    </Typography>
                </Box>
            ) : null}

            {event?.location ? (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Location
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {String(event.location)}
                    </Typography>
                </Box>
            ) : null}
        </Stack>
    );

    const renderBasicInfoFields = (values: EventFormValues) => (
        <>
            <Field autoFocus={!eventId} required name="eventName" component={FormTextInput} label="Event name" fullWidth />
            <Field name="clientName" component={FormTextInput} label="Client name" fullWidth />
            <Field name="location" component={FormTextInput} label="Location" fullWidth />
            <EventDatePickers values={values} />
        </>
    );

    const paperSx = {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        padding: { xs: 2, sm: 3 },
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.06)',
        boxSizing: 'border-box',
    };

    const containerSx: SxProps<Theme> = inTab
        ? {
              position: 'relative',
              boxSizing: 'border-box',
              width: '100%',
          }
        : {
              position: 'relative',
              minWidth: '300px',
              width: '100%',
              maxWidth: '400px',
              margin: '20px',
              padding: { xs: 2, sm: 3 },
              boxSizing: 'border-box',
          };

    const content = (
        <>
            {!inTab && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        {subtitle}
                    </Typography>
                    {canEdit && (
                        <ToggleButton value="edit-mode" selected={editMode} onClick={toggleEditMode}>
                            <EditIcon />
                        </ToggleButton>
                    )}
                </Box>
            )}

            {!editMode && inTab && (
                <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                mt: 2,
                                fontWeight: 500,
                                minWidth: 0,
                                flexShrink: 1,
                            }}
                        >
                            Basic Info
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            {canEdit && (
                                <ToggleButton value="edit-mode" selected={false} onClick={toggleEditMode} size="small">
                                    <EditIcon />
                                </ToggleButton>
                            )}
                        </Box>
                    </Box>

                    <Paper sx={paperSx}>{renderBasicInfoContent()}</Paper>

                    {event?.clientLogoUrl ? (
                        <>
                            <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 500, alignSelf: 'flex-start' }}>
                                Client Logo
                            </Typography>
                            <Paper sx={paperSx}>
                                <ClientLogoViewMode clientLogoUrl={event?.clientLogoUrl} />
                            </Paper>
                        </>
                    ) : null}

                    <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 500, alignSelf: 'flex-start' }}>
                        Event info
                    </Typography>
                    <Paper sx={paperSx}>
                        {(() => {
                            const docId = (event as { welcomeDocId?: string | null })?.welcomeDocId ?? null;
                            const tabId = (event as { welcomeTabId?: string | null })?.welcomeTabId ?? null;
                            const viewUrl = buildWelcomeDocViewUrl({ docId, tabId });
                            return viewUrl ? (
                                <Link href={viewUrl} target="_blank" rel="noopener noreferrer">
                                    Open info document in Google Docs
                                </Link>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No info document set.
                                </Typography>
                            );
                        })()}
                    </Paper>
                </Box>
            )}

            {!editMode && !inTab && (
                <>
                    <Box mt={2}>
                        <Divider sx={{ width: '100%', mb: 2 }} />
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
                            Basic Info
                        </Typography>

                        <Paper sx={paperSx}>{renderBasicInfoContent()}</Paper>
                    </Box>

                    {event?.clientLogoUrl ? (
                        <Box mt={2}>
                            <Divider sx={{ width: '100%', mb: 2 }} />
                            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
                                Client Logo
                            </Typography>
                            <Paper sx={paperSx}>
                                <ClientLogoViewMode clientLogoUrl={event?.clientLogoUrl} />
                            </Paper>
                        </Box>
                    ) : null}
                    <Box mt={2}>
                        <Divider sx={{ width: '100%', mb: 2 }} />
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
                            Event info
                        </Typography>
                        <Paper sx={paperSx}>
                            {(() => {
                                const docId = (event as { welcomeDocId?: string | null })?.welcomeDocId ?? null;
                                const tabId = (event as { welcomeTabId?: string | null })?.welcomeTabId ?? null;
                                const viewUrl = buildWelcomeDocViewUrl({ docId, tabId });
                                return viewUrl ? (
                                    <Link href={viewUrl} target="_blank" rel="noopener noreferrer">
                                        Open info document in Google Docs
                                    </Link>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No info document set.
                                    </Typography>
                                );
                            })()}
                        </Paper>
                    </Box>
                </>
            )}

            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={eventFormSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    const saveStartedAt = Date.now();
                    let watchdog: number | undefined;
                    try {
                        setSaveErrorMessage(null);
                        const token = user?.refresh_token;
                        if (!token) throw new Error('no token!');

                        // If submit gets stuck (e.g. hung fetch/db call), log a high-signal warning once.
                        watchdog = window.setTimeout(() => {
                            console.error('[EventForm] save still pending (watchdog)', {
                                eventId: targetEventId,
                                isCreating,
                                inTab,
                                viewOnly,
                                canEditEvents,
                                canEditMap,
                                elapsedMs: Date.now() - saveStartedAt,
                            });
                        }, 15_000);

                        let clientLogoUrl: string | null = null;
                        // Only upload when the user explicitly selected a new file this session (not from stale form state).
                        const logoFileCandidate = values.clientLogo;
                        if (logoFileCandidate instanceof File && (isCreating ? true : userSelectedNewLogo)) {
                            const logoFile = logoFileCandidate;
                            try {
                                clientLogoUrl = await getImageUrl(logoFile);
                                if (clientLogoUrl !== event?.clientLogoUrl) {
                                    // Delete old cached logo before uploading new one to prevent cache pollution
                                    if (event?.clientLogoUrl) {
                                        try {
                                            await deleteCachedImage(event.clientLogoUrl);
                                        } catch (error) {
                                            console.warn('Failed to delete old cached logo:', error);
                                            // Continue with upload even if cache cleanup fails
                                        }
                                    }
                                    setIsUploading(true);
                                    await cacheImage(logoFile, clientLogoUrl, token);
                                    await uploadImage(logoFile, clientLogoUrl, token);
                                } else {
                                    clientLogoUrl = event?.clientLogoUrl ?? null;
                                }
                            } catch (err) {
                                console.error('[EventForm] client logo upload failed', { eventId: targetEventId, error: err });
                                clientLogoUrl = event?.clientLogoUrl ?? null;
                            } finally {
                                setIsUploading(false);
                            }
                        } else if (values.clientLogo === null && event?.clientLogoUrl) {
                            // User explicitly removed the logo - clear from cache
                            try {
                                await deleteCachedImage(event.clientLogoUrl);
                            } catch (error) {
                                console.warn('Failed to delete cached logo:', error);
                            }
                            clientLogoUrl = null;
                        } else if (event?.clientLogoUrl) {
                            clientLogoUrl = event.clientLogoUrl;
                        }

                        const parsedFromRaw = parseWelcomeDocUrl(values.welcomeDocUrlRaw);
                        const welcomeDocIdToSave = parsedFromRaw.docId ?? values.welcomeDocId;
                        const welcomeTabIdToSave = parsedFromRaw.docId ? parsedFromRaw.tabId : values.welcomeTabId;

                        const otherTxns: TransactionChunk<AppSchema, any>[] = [
                            db.tx.events[targetEventId].update({
                                name: values.eventName,
                                clientLogoUrl: clientLogoUrl,
                                startDate: values.startDate?.getTime(),
                                endDate: values.endDate?.getTime(),
                                clientName: values.clientName || null,
                                location: values.location || null,
                                welcomeDocId: welcomeDocIdToSave || null,
                                welcomeTabId: welcomeTabIdToSave || null,
                            }),
                        ];

                        values.datasets.forEach(dataset => {
                            otherTxns.push(
                                db.tx.wasteDatasets[dataset.id || id()]
                                    .update({
                                        name: dataset.name || undefined,
                                        order: dataset.order,
                                    })
                                    .link({
                                        event: targetEventId,
                                    })
                            );
                        });

                        values.customMaterials.forEach(material => {
                            otherTxns.push(
                                db.tx.wasteMaterials[material.id || id()]
                                    .update({
                                        name: material.name,
                                        order: material.order,
                                        custom: true,
                                    })
                                    .link({
                                        forEvent: targetEventId,
                                        category: material.categoryId,
                                    })
                            );
                        });

                        // Only include map name updates if user has map-edit permission; otherwise
                        // the whole transaction would fail and block event-level changes (e.g. info link).
                        if (canEditMap) {
                            values.existingMaps.forEach(map => {
                                if (map.id) otherTxns.push(db.tx.maps[map.id].update({ name: map.name }));
                            });
                        }

                        // If creating an event with a groupId, link it to the group
                        if (isCreating && groupId) {
                            otherTxns.push(
                                db.tx.eventGroups[groupId].link({
                                    events: [targetEventId],
                                })
                            );
                            // Set the order for the new event
                            otherTxns.push(
                                db.tx.events[targetEventId].update({
                                    order: newEventOrder,
                                })
                            );
                        }

                        // When editing, only pass new maps that have an image so we never attempt
                        // map uploads when the user didn't add a new map with an image.
                        const newMapsToProcess = isCreating ? values.newMaps : values.newMaps.filter(m => m.bgImage != null);
                        const params = {
                            newMaps: newMapsToProcess,
                            eventId: targetEventId,
                            orderOffset: eventId ? numOtherMaps : 0,
                            otherTxns,
                            token,
                            setIsUploading,
                        };
                        await createMaps(params);

                        if (isCreating) {
                            if (groupId) {
                                navigate(`/group/${groupId}`);
                            } else {
                                navigate(`/event/${targetEventId}`);
                            }
                        } else {
                            setUserSelectedNewLogo(false);
                            setEditMode(false);
                        }
                    } catch (err) {
                        console.error('[EventForm] save failed', {
                            eventId: targetEventId,
                            isCreating,
                            inTab,
                            viewOnly,
                            canEditEvents,
                            canEditMap,
                            error: err,
                        });
                        if (process.env.NODE_ENV === 'development') {
                            console.error('[EventForm] save failed (raw error)', err);
                        }
                        showSaveError(formatUserSaveError(err));
                        // Keep edit mode so user can retry; avoid silent failure.
                    } finally {
                        if (watchdog !== undefined) window.clearTimeout(watchdog);
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, isSubmitting, setFieldValue, submitCount }) => (
                    <>
                        <EventFormValidationNotifier
                            submitCount={submitCount}
                            errors={errors}
                            eventId={targetEventId}
                            onValidationFailed={showSaveError}
                        />
                        <DiscardChangesDialog discard={discard} open={discardDialogOpen} onClose={closeDiscardDialog} />
                        <DirtyChecker dirty={hasBeenEdited} setDirty={setHasBeenEdited} />
                        <Form style={inTab ? { width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' } : undefined}>
                            <Stack direction="column" spacing={inTab ? 0 : 1} sx={{ paddingTop: inTab ? 0 : '10px' }}>
                                {editMode && inTab && (
                                    <>
                                        {shouldCenterHeaders ? (
                                            <Typography
                                                variant="h5"
                                                data-centered="true"
                                                sx={{
                                                    mt: 2,
                                                    mb: 2,
                                                    fontWeight: 500,
                                                    textAlign: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                Basic Info
                                            </Typography>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 2,
                                                    boxSizing: 'border-box',
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        mt: 2,
                                                        fontWeight: 500,
                                                        minWidth: 0,
                                                        flexShrink: 1,
                                                    }}
                                                >
                                                    Basic Info
                                                </Typography>
                                                {!isCreating && (
                                                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                                        <Button variant="outlined" onClick={toggleEditMode}>
                                                            Cancel
                                                        </Button>
                                                        <Button type="submit" variant="contained" disabled={isSubmitting}>
                                                            Save
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <Paper sx={paperSx}>
                                            <Stack spacing={2} direction="column">
                                                {renderBasicInfoFields(values)}
                                            </Stack>
                                        </Paper>

                                        {event?.clientLogoUrl || editMode ? (
                                            <>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        mt: 3,
                                                        mb: 1.5,
                                                        fontWeight: 500,
                                                        width: '100%',
                                                        textAlign: shouldCenterHeaders ? 'center' : 'left',
                                                    }}
                                                >
                                                    Client Logo
                                                </Typography>
                                                <Paper sx={paperSx}>
                                                    <ClientLogoEditMode
                                                        values={values}
                                                        setFieldValue={setFieldValue}
                                                        clientLogoUrl={event?.clientLogoUrl}
                                                        onNewLogoSelected={() => setUserSelectedNewLogo(true)}
                                                    />
                                                </Paper>
                                            </>
                                        ) : null}

                                        <Typography
                                            variant="h5"
                                            sx={{
                                                mt: 3,
                                                mb: 1.5,
                                                fontWeight: 500,
                                                width: '100%',
                                                textAlign: shouldCenterHeaders ? 'center' : 'left',
                                            }}
                                        >
                                            Event info
                                        </Typography>
                                        <Paper sx={paperSx}>
                                            <WelcomeDocInput
                                                value={{ docId: values.welcomeDocId, tabId: values.welcomeTabId }}
                                                onChange={welcomeRef => {
                                                    setFieldValue('welcomeDocId', welcomeRef.docId);
                                                    setFieldValue('welcomeTabId', welcomeRef.tabId);
                                                }}
                                                onRawChange={raw => setFieldValue('welcomeDocUrlRaw', raw)}
                                                label="Info document URL"
                                                helperText="Paste a Google Docs link; only document ID and optional tab are stored. Click Save to apply."
                                            />
                                        </Paper>
                                    </>
                                )}

                                {editMode && !inTab && (
                                    <>
                                        <Box mt={2}>
                                            <Divider sx={{ width: '100%', mb: 2 }} />
                                            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
                                                Basic Info
                                            </Typography>

                                            <Stack spacing={2} direction="column">
                                                {renderBasicInfoFields(values)}
                                            </Stack>
                                        </Box>
                                    </>
                                )}

                                {editMode && !inTab && (
                                    <ClientLogoEditMode
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        clientLogoUrl={event?.clientLogoUrl}
                                        onNewLogoSelected={() => setUserSelectedNewLogo(true)}
                                    />
                                )}

                                <Box mt={inTab ? 0 : 2}>
                                    {!inTab && <Divider sx={{ width: '100%', mb: 2 }} />}
                                    <Typography
                                        variant={inTab ? 'h5' : 'h6'}
                                        sx={{
                                            mt: 3,
                                            mb: 1.5,
                                            fontWeight: 500,
                                            width: '100%',
                                            textAlign: shouldCenterHeaders ? 'center' : 'left',
                                        }}
                                    >
                                        Maps
                                    </Typography>
                                    <Paper sx={paperSx}>
                                        {editMode ? (
                                            <Stack spacing={2}>
                                                {isCreating && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Maps are optional. You can add them now or later under Properties.
                                                    </Typography>
                                                )}
                                                <MapList
                                                    eventName={event?.name || values.eventName}
                                                    existingMaps={values.existingMaps}
                                                    editMode={true}
                                                />
                                                <AddNewMaps
                                                    newMaps={values.newMaps}
                                                    isSubmitting={isSubmitting}
                                                    numOtherMaps={numOtherMaps}
                                                />
                                            </Stack>
                                        ) : (
                                            <MapList
                                                eventName={event?.name || values.eventName}
                                                existingMaps={values.existingMaps}
                                                editMode={false}
                                            />
                                        )}
                                    </Paper>
                                </Box>

                                <Box mt={inTab ? 0 : 2}>
                                    {!inTab && <Divider sx={{ width: '100%', mb: 2 }} />}
                                    <Typography
                                        variant={inTab ? 'h5' : 'h6'}
                                        sx={{
                                            mt: 3,
                                            mb: 1.5,
                                            fontWeight: 500,
                                            width: '100%',
                                            textAlign: shouldCenterHeaders ? 'center' : 'left',
                                        }}
                                    >
                                        Waste Datasets
                                    </Typography>
                                    <Paper sx={paperSx}>
                                        <DatasetList
                                            eventName={event?.name || values.eventName}
                                            datasets={values.datasets}
                                            isSubmitting={isSubmitting}
                                            editMode={editMode}
                                        />
                                    </Paper>
                                </Box>

                                <Box mt={inTab ? 0 : 2}>
                                    {!inTab && <Divider sx={{ width: '100%', mb: 2 }} />}
                                    <Typography
                                        variant={inTab ? 'h5' : 'h6'}
                                        sx={{
                                            mt: 3,
                                            mb: 1.5,
                                            fontWeight: 500,
                                            width: '100%',
                                            textAlign: shouldCenterHeaders ? 'center' : 'left',
                                        }}
                                    >
                                        Custom Materials
                                    </Typography>
                                    <Paper sx={paperSx}>
                                        <CustomMaterialList
                                            eventName={event?.name || values.eventName}
                                            customMaterials={values.customMaterials}
                                            isSubmitting={isSubmitting}
                                            editMode={editMode}
                                        />
                                    </Paper>
                                </Box>

                                {editMode && (
                                    <>
                                        <Box mt={3} mb={3}>
                                            <Stack direction="row" sx={{ justifyContent: 'center' }}>
                                                <Button type="submit" variant="contained" disabled={isSubmitting}>
                                                    {isCreating ? 'Create Event' : 'Save All Changes'}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Form>
                    </>
                )}
            </Formik>
            <Snackbar
                open={saveErrorMessage != null}
                autoHideDuration={10_000}
                onClose={() => setSaveErrorMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSaveErrorMessage(null)} severity="error" sx={{ width: '100%', maxWidth: 560 }}>
                    {saveErrorMessage}
                </Alert>
            </Snackbar>
            {maybeUploadSpinner}
        </>
    );

    if (inTab) {
        return <Box sx={containerSx}>{content}</Box>;
    }

    return (
        <Paper sx={containerSx} elevation={4}>
            {content}
        </Paper>
    );
}
